<template>
    <div v-if="useCachedInsight && insightData">
        <CalendarInsightCard :class="insightCardClasses" :insight-data="insightData" :cta-loading="startingChat" @cta-button-click="handleDiveDeeper" />
    </div>
    <Transition v-else :name="transitionName" @enter="onTransitionEnter">
        <div v-if="insightData" key="content" class="expand-horizontal-content flex flex-col">
            <Transition name="fade-from-top" mode="out-in">
                <div v-if="!transitionElapsed">
                    <UserCalendarSkeleton class="h-full flex-1" :section-count="1" />
                </div>
                <CalendarInsightCard
                    v-else-if="insightData"
                    :class="insightCardClasses"
                    :insight-data="insightData"
                    :cta-loading="startingChat"
                    @cta-button-click="handleDiveDeeper" />
            </Transition>
        </div>
    </Transition>
</template>

<script setup lang="ts">
import { breakpointsTailwind, useBreakpoints } from "@vueuse/core";
import { useFetch } from "~vue/api";
import CalendarInsightCard from "~vue/CalendarInsights/CalendarInsightCard.vue";
import UserCalendarSkeleton from "~vue/components/CalendarIntegration/UserCalendarSkeleton.vue";
import { useCalendarStore } from "~vue/stores/calendarStore";
import {
    type CalendarInsightsChatPromptCallCode,
    CalendarInsightSchema,
    type CalendarInsightsResponse,
    type CreateCalendarInsightsConversationRequest,
    type CreateCalendarInsightsConversationResponse,
} from "~vue/types/calendar_integration";
import { openUrl } from "~vue/utils";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, ref } from "vue";

// 4 hours is the current window outside of which a new insight will be generated
// This is a shorter cache time that will cause the insight to be refetched more frequently
const CACHE_EXPIRATION_MS = 1000 * 60 * 60 * 2; // 2 hours

const cachedInsight = useCalendarStore.cachedCalendarInsight;

const cacheIsExpired = computed(() => {
    return cachedInsight && cachedInsight.cachedAt < Date.now() - CACHE_EXPIRATION_MS;
});

const useCachedInsight = computed(() => cachedInsight?.insight && !cacheIsExpired.value);

const isMd = useBreakpoints(breakpointsTailwind).greaterOrEqual("md");
const transitionName = computed(() => (isMd.value ? "expand-horizontal" : "expand-vertical"));

const extraLogData = computed(() => ({
    insight_id: insightData.value?.insight_id,
    insight_title: insightData.value?.title,
}));

// Trying to use the @after-enter hook to set the transitionElapsed state to true results in too long of a delay due to the way the
// browser triggers the after-enter hook for a transition. Instead, a value to be used in a setTimeout is found empricially based on
// the duration of the transition as set by the CSS params. When changing the CSS params, the value here needs to be updated as well
const expandTransitionDurationMs = 800;
const transitionElapsed = ref(false);
function onTransitionEnter() {
    setTimeout(() => {
        transitionElapsed.value = true;
        logUserInteraction(AmplitudeEventNames.HOMEPAGE_CALENDAR_INSIGHT_SHOWN, extraLogData.value);
    }, expandTransitionDurationMs);
}

const diveDeeperPromptCallCode: CalendarInsightsChatPromptCallCode = "calendar_insights_dive_deeper";

// For now, if the first load of insights fails, we don't try again.
const { data: calendarInsightsData, onFetchResponse: onCalendarInsightsResponse } = useFetch(
    computed(() => `/api/calendar_integration/insights`),
    { immediate: !useCachedInsight.value },
).json<CalendarInsightsResponse>();

// Setup useFetch for creating calendar insights conversation (POST request)
const conversationPayload = ref<CreateCalendarInsightsConversationRequest | null>(null);
const {
    execute: executeCreateConversation,
    onFetchError: onCreateConversationError,
    data: conversationData,
    onFetchResponse: onCreateConversationResponse,
} = useFetch("/api/calendar_integration/insights/conversation/", { immediate: false }).post(conversationPayload).json<CreateCalendarInsightsConversationResponse>();

const createConversationError = ref(false);

const insightCardClasses = computed(() => {
    return "flex-1 min-h-0 h-full";
});

onCalendarInsightsResponse(() => {
    if (calendarInsightsData.value?.insight) {
        useCalendarStore.setCachedCalendarInsight(calendarInsightsData.value.insight);
    }
});

// Use the startingChat rather than isCreatingConversation to avoid delay between API call
// and openUrl call
const startingChat = ref(false);

const insightData = computed(() => {
    const raw = useCachedInsight.value ? cachedInsight?.insight : calendarInsightsData.value?.insight;
    if (raw == null) {
        return undefined;
    }
    const parsed = CalendarInsightSchema.safeParse(raw);
    return parsed.success ? parsed.data : undefined;
});

onCreateConversationResponse(() => {
    if (conversationData.value?.coaching_session_url) {
        // Navigate to the coaching session
        openUrl(conversationData.value.coaching_session_url);
    }
});

onCreateConversationError(() => {
    createConversationError.value = true;
    startingChat.value = false;
});

function handleDiveDeeper() {
    const insight = insightData.value;
    if (!insight) {
        return;
    }

    logUserInteraction(AmplitudeEventNames.HOMEPAGE_CALENDAR_INSIGHT_DIVE_DEEPER_CLICKED, extraLogData.value);

    createConversationError.value = false;
    startingChat.value = true;

    conversationPayload.value = {
        insight_id: insight.insight_id,
        prompt_call_code: diveDeeperPromptCallCode,
    };

    executeCreateConversation();
}
</script>

<style scoped>
.expand-horizontal-enter-from,
.expand-horizontal-leave-to {
    max-width: 0%;
    opacity: 0;
}

.expand-horizontal-enter-to,
.expand-horizontal-leave-from {
    max-width: 100%;
}

/* When changing the CSS params, be sure to update the expandTransitionDurationMs value above (see comment above for more details) */
.expand-horizontal-enter-active,
.expand-horizontal-leave-active {
    overflow: hidden;
    transition:
        max-width 3s cubic-bezier(0.4, 0.5, 0.25, 1),
        opacity 1s ease;
}

/* Vertical expand (small screens) */
.expand-vertical-enter-from,
.expand-vertical-leave-to {
    max-height: 0;
    opacity: 0;
}

.expand-vertical-enter-to,
.expand-vertical-leave-from {
    max-height: 600px;
}

/* When changing the CSS params, be sure to update the expandTransitionDurationMs value above */
.expand-vertical-enter-active,
.expand-vertical-leave-active {
    overflow: hidden;
    transition:
        max-height 3s cubic-bezier(0.4, 0.5, 0.25, 1),
        opacity 1s ease;
}

/* inner content swap */
.fade-from-top-enter-active,
.fade-from-top-leave-active {
    transition:
        opacity 220ms ease,
        transform 260ms cubic-bezier(0.3, 0.7, 0.25, 1);
}

.fade-from-top-enter-from,
.fade-from-top-leave-to {
    opacity: 0;
    transform: translateY(-6px);
}

.fade-from-top-enter-to,
.fade-from-top-leave-from {
    opacity: 1;
    transform: translateY(0);
}
</style>
